/** Target format for voice uploads stored in the portal. */
export const VOICE_WAV_SAMPLE_RATE = 44100;
export const VOICE_WAV_BITS = 16;

function readFourCC(view: DataView, offset: number): string {
  let s = "";
  for (let i = 0; i < 4; i++) s += String.fromCharCode(view.getUint8(offset + i));
  return s;
}

/**
 * Detect PCM WAV already at 44.1 kHz, 16-bit, mono — the canonical voice prompt
 * format used by the portal. Stereo or non‑16‑bit files take the slow path so
 * they get re-encoded to the canonical shape (and stay under the upload limit).
 * Walks chunk list so non-canonical headers still work.
 */
export function isStandardPcm44100Wav16(arrayBuffer: ArrayBuffer): boolean {
  if (arrayBuffer.byteLength < 36) return false;
  const v = new DataView(arrayBuffer);
  if (readFourCC(v, 0) !== "RIFF" || readFourCC(v, 8) !== "WAVE") return false;
  let off = 12;
  while (off + 8 <= arrayBuffer.byteLength) {
    const id = readFourCC(v, off);
    const size = v.getUint32(off + 4, true);
    const chunkStart = off + 8;
    if (id === "fmt ") {
      if (size < 16 || chunkStart + size > arrayBuffer.byteLength) return false;
      const format = v.getUint16(chunkStart, true);
      const numChannels = v.getUint16(chunkStart + 2, true);
      const sampleRate = v.getUint32(chunkStart + 4, true);
      const bitsPerSample = v.getUint16(chunkStart + 14, true);
      return (
        format === 1 &&
        bitsPerSample === 16 &&
        sampleRate === VOICE_WAV_SAMPLE_RATE &&
        numChannels === 1
      );
    }
    off += 8 + size + (size % 2);
  }
  return false;
}

function writeString(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
}

/** Float32 AudioBuffer (any rate) → 16-bit PCM WAV ArrayBuffer. */
export function audioBufferToWavPcm16(buffer: AudioBuffer): ArrayBuffer {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const length = buffer.length;
  const bitsPerSample = 16;
  const blockAlign = (numChannels * bitsPerSample) / 8;
  const byteRate = sampleRate * blockAlign;
  const dataSize = length * blockAlign;
  const wav = new ArrayBuffer(44 + dataSize);
  const view = new DataView(wav);
  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  writeString(view, 36, "data");
  view.setUint32(40, dataSize, true);
  const out = new Int16Array(wav, 44, length * numChannels);
  for (let i = 0; i < length; i++) {
    for (let c = 0; c < numChannels; c++) {
      const s = Math.max(-1, Math.min(1, buffer.getChannelData(c)[i]));
      out[i * numChannels + c] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
  }
  return wav;
}

function limitChannels(buf: AudioBuffer, maxCh: number): AudioBuffer {
  if (buf.numberOfChannels <= maxCh) return buf;
  const { length, sampleRate } = buf;
  const out = new AudioBuffer({ length, numberOfChannels: maxCh, sampleRate });
  for (let c = 0; c < maxCh; c++) {
    out.copyToChannel(Float32Array.from(buf.getChannelData(c)), c);
  }
  return out;
}

export type VoiceWavProgress = (percent: number, message: string) => void;

/**
 * Returns a File of 44.1 kHz, 16-bit PCM WAV. Uses a fast path when the input
 * is already in that format. Otherwise decodes via Web Audio and resamples.
 */
export async function ensure44100Hz16BitWavFile(
  file: File,
  onProgress?: VoiceWavProgress,
): Promise<File> {
  const report = (pct: number, message: string) => onProgress?.(pct, message);

  report(4, "Reading file…");
  const arrayBuffer = await file.arrayBuffer();

  report(12, "Checking format…");
  if (isStandardPcm44100Wav16(arrayBuffer)) {
    report(100, "Ready to upload.");
    const name = /\.wav$/i.test(file.name) ? file.name : `${file.name.replace(/\.[^/.]+$/, "")}.wav`;
    return new File([arrayBuffer], name, { type: "audio/wav" });
  }

  report(22, "Decoding audio…");
  const ctx = new AudioContext();
  let decoded: AudioBuffer;
  try {
    decoded = await ctx.decodeAudioData(arrayBuffer.slice(0));
  } finally {
    await ctx.close().catch(() => undefined);
  }

  // Voice prompts in this portal are stored as mono — collapsing here halves
  // the upload size and matches what the legacy DB blob has always been.
  decoded = limitChannels(decoded, 1);
  const channels = decoded.numberOfChannels;
  const duration = decoded.duration;
  const offlineLength = Math.max(1, Math.ceil(duration * VOICE_WAV_SAMPLE_RATE));
  report(38, "Converting to 44.1 kHz, 16-bit mono…");
  const offline = new OfflineAudioContext(channels, offlineLength, VOICE_WAV_SAMPLE_RATE);
  const src = offline.createBufferSource();
  src.buffer = decoded;
  src.connect(offline.destination);
  src.start(0);
  const rendered = await offline.startRendering();

  report(72, "Encoding WAV…");
  const wavBytes = audioBufferToWavPcm16(rendered);
  report(88, "Ready to upload.");
  const base = file.name.replace(/\.[^/.]+$/, "") || "voice";
  return new File([wavBytes], `${base}.wav`, { type: "audio/wav" });
}
